import { useComponentCssInjection } from "@salt-ds/styles";
import { useWindow } from "@salt-ds/window";
import { TableCellRendererProps } from "@vuu-ui/vuu-table-types";
import {
  dataAndColumnUnchanged,
  DOWN1,
  DOWN2,
  isTypeDescriptor,
  registerComponent,
  UP1,
  UP2,
} from "@vuu-ui/vuu-utils";
import cx from "clsx";
import { memo } from "react";
import { useDirection } from "./useDirection";

import { DataValueType } from "@vuu-ui/vuu-data-types";
import backgroundCellCss from "./BackgroundCell.css";
import backgroundKeyFramesCss from "./BackgroundKeyframes.css";

const CHAR_ARROW_UP = String.fromCharCode(11014);
const CHAR_ARROW_DOWN = String.fromCharCode(11015);

const classBase = "vuuBackgroundCell";

// TODO these sre repeated from PriceFormatter - where shoud they live ?
const FlashStyle = {
  ArrowOnly: "arrow",
  BackgroundOnly: "bg-only",
  ArrowBackground: "arrow-bg",
};

const getFlashStyle = (colType?: DataValueType) => {
  if (isTypeDescriptor(colType) && colType.renderer) {
    if ("flashStyle" in colType.renderer) {
      return colType.renderer["flashStyle"];
    }
  }
  return FlashStyle.BackgroundOnly;
};

export const BackgroundCell = memo(function BackgroundCell({
  column,
  dataRow,
}: TableCellRendererProps) {
  //TODO what about click handling

  const targetWindow = useWindow();
  useComponentCssInjection({
    testId: "vuu-background-cell",
    css: backgroundCellCss,
    window: targetWindow,
  });
  useComponentCssInjection({
    testId: "vuu-background-keyframes",
    css: backgroundKeyFramesCss,
    window: targetWindow,
  });

  const { name, type, valueFormatter } = column;
  const value = dataRow[name];
  const flashStyle = getFlashStyle(type);
  const { direction, hot, cooling } = useDirection(dataRow.key, value, column);
  const arrow =
    flashStyle === FlashStyle.ArrowOnly ||
    flashStyle === FlashStyle.ArrowBackground
      ? hot === "up" || direction === UP1 || direction === UP2
        ? CHAR_ARROW_UP
        : hot === "down" || direction === DOWN1 || direction === DOWN2
          ? CHAR_ARROW_DOWN
          : null
      : null;

  const dirClass = direction ? ` ` + direction : "";

  const className = cx(classBase, dirClass, {
    [`${classBase}-backgroundOnly`]: flashStyle === FlashStyle.BackgroundOnly,
    [`${classBase}-arrowOnly`]: flashStyle === FlashStyle.ArrowOnly,
    [`${classBase}-arrowBackground`]: flashStyle === FlashStyle.ArrowBackground,
    [`${classBase}-hotUp`]: hot === "up",
    [`${classBase}-hotDown`]: hot === "down",
    [`${classBase}-coolUp`]: cooling === "up",
    [`${classBase}-coolDown`]: cooling === "down",
  });

  const formattedValue = valueFormatter(value);

  return (
    <div className={className} tabIndex={-1}>
      {flashStyle === FlashStyle.BackgroundOnly ? (
        // the flash colour sits on an overlay whose opacity fades, which
        // Chrome runs on the compositor
        <span aria-hidden className={`${classBase}-flash`} />
      ) : (
        <div className={`${classBase}-arrow`}>{arrow}</div>
      )}
      <span className={`${classBase}-value`}>{formattedValue}</span>
    </div>
  );
}, dataAndColumnUnchanged);

registerComponent(
  "vuu.price-move-background",
  BackgroundCell,
  "cell-renderer",
  {
    description: "Change background color of cell when value changes",
    configEditor: "BackgroundCellConfigurationEditor",
    label: "Background Flash",
    serverDataType: [
      "long",
      "int",
      "double",
      "scaleddecimal2",
      "scaleddecimal4",
      "scaleddecimal6",
      "scaleddecimal8",
    ],
  },
);
